import React, { type ReactNode } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { cn } from '../../components/ui/cn';
import { useLanguage } from '../../lib/i18n';

/**
 * Entegrasyon ekranının parçaları.
 *
 * İki görünüm var: bağlı olanlar sade bir satır listesi ("Entegrasyonlarım"),
 * bağlı olmayanlar kart ızgarası ("Keşfet"). İkisinde de tıklama aynı yere
 * çıkıyor — o entegrasyonun ayrıntı penceresi.
 */

type BadgeTone = 'ok' | 'brand' | 'muted' | 'warn';

const BADGE_TONE: Record<BadgeTone, string> = {
  ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500',
  brand: 'border-brand/30 bg-brand/10 text-brand',
  muted: 'border-border bg-muted text-muted-foreground',
  warn: 'border-amber-500/30 bg-amber-500/10 text-amber-500',
};

export const Badge: React.FC<{ tone?: BadgeTone; children: ReactNode }> = ({ tone = 'muted', children }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
      BADGE_TONE[tone]
    )}
  >
    {children}
  </span>
);

/** Simge kutusu — her entegrasyonun kendi rengi, zemin ortak. */
export const IconTile: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <span
    className={cn(
      'flex size-10 shrink-0 items-center justify-center rounded-xl border border-border bg-field [&_svg]:size-5',
      className
    )}
  >
    {children}
  </span>
);

export interface IntegrationAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  variant?: 'default' | 'outline';
}

export interface IntegrationEntry {
  id: string;
  name: string;
  icon: ReactNode;
  /** Keşfet kartında ve ayrıntı penceresinin başlığı altında görünür. */
  description: string;
  /** Bağlıysa "Entegrasyonlarım" listesinde, değilse "Keşfet" ızgarasında. */
  connected: boolean;
  /** Bağlı satırında adın altındaki hesap/durum satırı. */
  detail?: string;
  badge?: ReactNode;
  /** Keşfet kartının sağ üstündeki düğme. */
  action?: IntegrationAction;
  /** Ayrıntı penceresinin içeriği. */
  body?: ReactNode;
  /** Ayrıntı penceresinin alt düğmeleri. */
  footer?: ReactNode;
}

/** Bağlı entegrasyon satırı: simge, ad, hesap ve sağda ok. */
export const ConnectedRow: React.FC<{ entry: IntegrationEntry; onOpen: () => void }> = ({ entry, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="group flex w-full items-center gap-3.5 rounded-xl px-3 py-3 text-left transition-colors hover:bg-accent/60"
  >
    <IconTile>{entry.icon}</IconTile>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <span className="truncate font-medium text-foreground">{entry.name}</span>
        {entry.badge}
      </span>
      {entry.detail && <span className="mt-0.5 block truncate text-[0.8rem] text-muted-foreground">{entry.detail}</span>}
    </span>
    <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
  </button>
);

/** Keşfet kartı: sol üstte simge, sağ üstte düğme, altında ad ve açıklama. */
export const DiscoverCard: React.FC<{ entry: IntegrationEntry; onOpen: () => void }> = ({ entry, onOpen }) => {
  const { t } = useLanguage();
  const { action } = entry;

  return (
    <div className="relative flex flex-col rounded-xl border border-border bg-card p-4 transition-colors hover:border-brand/40 hover:bg-accent/30">
      {/* Kartın her yeri ayrıntı penceresini açar; düğme bunun üstünde durur. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={t(`${entry.name} ayrıntıları`, `${entry.name} details`)}
        className="absolute inset-0 rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
      />

      <div className="flex items-start justify-between gap-3">
        <IconTile>{entry.icon}</IconTile>
        {action && (
          <Button
            size="sm"
            variant={action.variant ?? 'outline'}
            disabled={action.disabled}
            onClick={action.onClick}
            className="relative"
          >
            {action.busy && <Loader2 className="size-4 animate-spin" />}
            {action.label}
          </Button>
        )}
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        <h3 className="font-semibold leading-none text-foreground">{entry.name}</h3>
        {entry.badge}
      </div>
      <p className="mt-1.5 text-[0.8rem] leading-relaxed text-muted-foreground">{entry.description}</p>
    </div>
  );
};

/** Pencere içinde tek satırlık ayar (anahtar, düğme). */
export const DetailRow: React.FC<{ title: string; description?: string; children?: ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <div className="flex items-start justify-between gap-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
    <div className="min-w-0 space-y-1">
      <p className="text-sm font-medium leading-none text-foreground">{title}</p>
      {description && <p className="text-[0.8rem] leading-relaxed text-muted-foreground">{description}</p>}
    </div>
    {children && <div className="flex shrink-0 items-center gap-2">{children}</div>}
  </div>
);

/** Eksik .env anahtarı / uyarı notu. */
export const SetupNote: React.FC<{ children: ReactNode }> = ({ children }) => (
  <p className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-[0.8rem] leading-relaxed text-amber-500">
    {children}
  </p>
);
